import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, OnDestroy, Optional, Renderer2, RendererFactory2 } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from '@ngx-translate/core';
import { RUNNING_IN_LEAF_HOST } from "app/leaf/leaf-context.token";
import { redirectAcrossReactBoundary } from "app/leaf/leaf-guard-redirect";
import { LgpdAceiteModal } from "app/pages/full-pages/customs/modals/lgpd-aceite-modal/lgpd-aceite-modal";
import { SidebarService } from "app/shared/sidebar/sidebar.service";
import { Observable, Subject, map, takeUntil } from "rxjs";
import { ConfigModulos } from "../../../models/configmodulos.model";
import { ConfigPSIM } from "../../../models/configpsim.model";
import { Configuracoes } from "../../../models/configuracoes.model";
import { AuthService, IID } from "../../../shared/auth/auth.service";
import { AbTenant } from "app/abstract/full-pages/admin/tenant.abstract";
import { AbLogin } from "app/abstract/content-pages/login/login.abstract";
import { AbToastr } from "app/abstract/services/toastr.abstract";
import { AbUsuario } from "app/abstract/full-pages/admin/usuario.abstract";
import { AbConfigModulos } from "app/abstract/full-pages/config-modulos/config-modulos.abstract";
import { AbConfigPsim } from "app/abstract/full-pages/config-psim/config-psim.abstract";
import { AbPessoa } from "app/abstract/full-pages/pessoa/pessoa.abstract";
import { AbIntegrador } from "app/abstract/full-pages/integrador/integrador.abstract";
import { AbConfigSkin } from "app/abstract/full-pages/config-skin/config-skin.abstract";
import { AbConfiguracoes } from "app/abstract/full-pages/configuracoes/configuracoes.abstract";
import { AbConta } from "app/abstract/full-pages/conta/conta.abstract";
import { AbLgpd } from "app/abstract/full-pages/lgpd/lgpd.abstract";
import { AbLocale } from "app/abstract/full-pages/locale/locale.abstract";
import { AbPostHog } from "app/abstract/services/posthog.abstract";
import { IdentificacaoPostHogService } from "app/services/posthog/identificacao-posthog.service";
import { PostHogEventEnum } from "enums/posthog-event.enum";
import { AbFeatureFlag } from "app/abstract/services/feature-flag.abstract";
import { Confirm2FAPageComponent } from "../confirm-2FA/confirm-2FA-page.component";
import { SelectMfaFactorComponent, MfaFactor } from "../select-mfa-factor/select-mfa-factor.component";
import { HASH_MFA } from "app/const";
import { FeatureFlagChave, StatusFeatureFlag } from "enums/feature-flag.enum";
import { environment } from "environments/environment";

type MotivoLoginFalhou =
  | 'credenciais_invalidas'
  | 'mfa_invalido'
  | 'usuario_bloqueado'
  | 'integrador_nao_autorizado'
  | 'erro_servidor'
  | 'erro_rede'
  | 'timeout';

// Na v2 todos os erros de negócio do login chegam como 406; só a key do corpo distingue o caso.
const MOTIVO_POR_CHAVE_ERRO: Record<string, MotivoLoginFalhou> = {
  CREDENCIAIS_INVALIDAS: 'credenciais_invalidas',
  USUARIO_BLOQUEADO: 'usuario_bloqueado',
  MSG_ACESSO_INTEGRADOR_URL_NAO_AUTORIZADO: 'integrador_nao_autorizado'
};

const MOTIVO_POR_ERRO_V1: Record<string, MotivoLoginFalhou> = {
  tentativa_login: 'usuario_bloqueado',
  integrador_access_denied: 'integrador_nao_autorizado'
};

@Injectable({ providedIn: 'root' })
export class LoginService extends AbLogin implements OnDestroy {
  skin: any = [];
  redirecAccountList: any;
  doNotRedirectAccountList: any = false;
  refreshAttempt: number = 0;
  basicWeb = "Basic Y2VudGVyLXdlYjphcGktc2VjcmV0";

  public renderer2: Renderer2

  private readonly destroy$ = new Subject();

  constructor(
    http: HttpClient,
    private authService: AuthService,
    private toastrService: AbToastr,
    private tenantService: AbTenant,
    private router: Router,
    private sidebarService: SidebarService,
    private userService: AbUsuario,
    private configModulesService: AbConfigModulos,
    private configPSIMService: AbConfigPsim,
    private personService: AbPessoa,
    private integratorService: AbIntegrador,
    private configSkinService: AbConfigSkin,
    rendererFactory: RendererFactory2,
    private configService: AbConfiguracoes,
    private accountService: AbConta,
    private modalService: NgbModal,
    private lgpdService: AbLgpd,
    private translateService: TranslateService,
    private localeService: AbLocale,
    private posthog: AbPostHog,
    private featureFlagService: AbFeatureFlag,
    private identificacaoPostHog: IdentificacaoPostHogService,
    @Optional() @Inject(RUNNING_IN_LEAF_HOST) private readonly isRunningInLeafHost: boolean | null
  ) {
    super(http);
    this.renderer2 = rendererFactory.createRenderer(null, null);
  }

  doLogin(login: any) {
    const data = {
      username: login.username,
      password: login.password
    };

    return this.doWebLogin(data);
  }

  refreshToken(accountSelected?, _redirectAccountList?, _doNotRedirectAccountList?) {
    this.refreshAttempt = 0;
    this.redirecAccountList = _redirectAccountList;

    if (_doNotRedirectAccountList == true) {
      this.doNotRedirectAccountList = _doNotRedirectAccountList;
    } else {
      this.doNotRedirectAccountList = false;
    }

    if (accountSelected) {
      this.tenantService.salvarTenantStorage(accountSelected);
      this.tenantService.salvarNomeTenantStorage(accountSelected.nomeConta)
    }

    this.doRefreshSession(accountSelected);
  }

  openMfaModal(hashMfa: string, fatores: MfaFactor[], data: any) {
    this.posthog.capture(PostHogEventEnum.MFA_SOLICITADO);
    if (fatores.length === 1) {
      this.openTokenModal(hashMfa, fatores[0].tipo, fatores[0].identificador, data, false);
    } else {
      this.openFactorSelectionModal(hashMfa, fatores, data);
    }
  }

  private openFactorSelectionModal(hashMfa: string, fatores: MfaFactor[], data: any) {
    const modalRef = this.modalService.open(SelectMfaFactorComponent, {
      windowClass: 'fadeInDown'
    });
    fatores.forEach(fator => {
      if (fator.tipo == "EMAIL") {
        fator.identificador = this.mascararEmail(fator.identificador);
      }
    })
    modalRef.componentInstance.fatores = fatores;

    modalRef.result.then((selectedFactor: MfaFactor) => {
      this.enviarTokenMfa(hashMfa, selectedFactor.tipo).pipe(takeUntil(this.destroy$)).subscribe(
        () => {
          this.toastrService.success(this.translateService.instant("Código enviado com sucesso"));
          this.openTokenModal(hashMfa, selectedFactor.tipo, selectedFactor.identificador, data, true)
        },
        () => this.toastrService.error(this.translateService.instant("Erro ao enviar código de verificação"))
      );
    }).catch(() => { });
  }

  private openTokenModal(hashMfa: string, fatorSelecionado: string, identificadorFator: string, data: any, temMultiplosFatores: boolean = false) {
    const modalRef = this.modalService.open(Confirm2FAPageComponent, {
      windowClass: 'fadeInDown'
    });
    modalRef.componentInstance.login = data;
    modalRef.componentInstance.hashMfa = hashMfa;
    modalRef.componentInstance.fatorSelecionado = fatorSelecionado;
    modalRef.componentInstance.identificadorFator = identificadorFator;
    modalRef.componentInstance.temMultiplosFatores = temMultiplosFatores;
  }

  validarTokenMfa(hashMfa: string, tokenMfa: string): Observable<any> {
    const dadosEncoded = `hashMfa=${encodeURIComponent(hashMfa)}&token=${encodeURIComponent(tokenMfa)}`;

    const headers = new HttpHeaders()
      .set("Authorization", this.basicWeb)
      .set("Content-Type", "application/x-www-form-urlencoded");

    this.toastrService.off();
    const rota = this.usarLoginV2() ? "auth/web/v2/validar-token-mfa" : "auth/web/validar-token-mfa";
    return this.http.post(this.url + rota, dadosEncoded, { headers })
      .pipe(map((resposta) => this.semCamposLoginV2(resposta)));
  }

  enviarTokenMfa(hashMfa: string, fator: string): Observable<any> {
    const dadosEncoded = `hashMfa=${encodeURIComponent(hashMfa)}`;

    const headers = new HttpHeaders()
      .set("Authorization", this.basicWeb)
      .set("Content-Type", "application/x-www-form-urlencoded");
    return this.http.post(this.url + `auth/web/enviar-token-mfa/${fator}`, dadosEncoded, { headers });
  }

  private doWebLogin(data: any) {
    let dadosEncoded = `username=${encodeURIComponent(data.username)}&password=${encodeURIComponent(data.password)}`;

    const iid = sessionStorage.getItem(IID);
    if (iid) {
      dadosEncoded += `&integradorId=${iid}`;
    }

    const hash = localStorage.getItem(HASH_MFA);
    if (hash) {
      dadosEncoded += `&hashMfa=${hash}`;
    }

    const headers = new HttpHeaders()
      .set("Authorization", this.basicWeb)
      .set("Content-Type", "application/x-www-form-urlencoded");

    this.toastrService.on();
    this.toastrService.toastr.previousToastMessage = '';

    const rota = this.usarLoginV2() ? "auth/web/v2/login" : "auth/web/login";

    return new Observable((observer) => {
      this.http.post(this.url + rota, dadosEncoded, { headers })
        .pipe(takeUntil(this.destroy$.asObservable()))
        .subscribe({
          next: (response) => {
            observer.complete();
            const loginResponse: any = this.semCamposLoginV2(response);
            if (loginResponse?.autenticacaoDeDoisFatores) {
              localStorage.setItem(HASH_MFA, loginResponse.hashMfa);
              this.openMfaModal(loginResponse.hashMfa, loginResponse.fatores, data);
            } else {
              this.loadUser(loginResponse, null, true);
            }
          },
          error: (error) => {
            this.posthog.capture(PostHogEventEnum.LOGIN_FALHOU, {
              motivo: this.inferirMotivoLoginFalhou(error)
            });
            observer.complete();
          }
        });
    });
  }

  private doRefreshSession(accountSelected: any) {
    const dadosEncoded = `refresh_token=${encodeURIComponent(this.authService.getRefreshToken())}`;

    const headers = new HttpHeaders()
      .set("Authorization", this.basicWeb)
      .set("Content-Type", "application/x-www-form-urlencoded");

    if (this.retry()) {
      this.toastrService.off();
    } else {
      this.toastrService.on();
      this.toastrService.toastr.previousToastMessage = '';
    }

    this.http.post(this.url + "auth/web/refresh-session", dadosEncoded, { headers }).pipe(takeUntil(this.destroy$)).subscribe(token => {
      this.toastrService.on();
      this.loadUser(token, accountSelected, false);
    }, () => {
      if (this.retry()) {
        this.refreshAttempt++;
        setTimeout(() => {
          this.doRefreshSession(accountSelected);
        }, 600);
      } else {
        this.posthog.capture(PostHogEventEnum.LOGIN_FALHOU, { motivo: 'erro_servidor' as MotivoLoginFalhou });
        this.tenantService.limparTenantStorage();
      }
    });
  }

  public retry(): boolean {
    return this.refreshAttempt < 2;
  }

  private sincronizarCapabilitiesConta() {
    if (!this.tenantService.getTenantStorage()) {
      this.featureFlagService.limparCapabilitiesConta();
      return;
    }
    this.featureFlagService.carregarCapabilitiesConta().pipe(takeUntil(this.destroy$)).subscribe();
  }

  public loadUser(token, accountSelected, doRefresh) {
    this.authService.saveToken(token);
    this.featureFlagService.carregarCapabilitiesPrivadas().subscribe();
    this.sincronizarCapabilitiesConta();
    this.userService.getUsuario().pipe(takeUntil(this.destroy$)).subscribe(
      user => {
        this.modalService.dismissAll();
        if (user == null) {
          this.toastrService.warning(this.translateService.instant("O acesso à conta está bloqueado temporariamente."));
          return
        }

        this.identificarUsuarioPostHog(user, accountSelected);

        if (doRefresh) {
          this.posthog.capture(PostHogEventEnum.LOGIN_SUCESSO);
        }
        this.modalLGPD(user, accountSelected, doRefresh);
      },
      err => {
        this.posthog.capture(PostHogEventEnum.LOGIN_FALHOU, { motivo: 'erro_servidor' as MotivoLoginFalhou });
        this.authService.logout();
        this.toastrService.error(err);
      });
  }

  public modalLGPD(user, accountSelected, doRefresh) {
    if (!user.lgpdAceite) {
      this.registerUser(user, accountSelected, doRefresh);
      return;
    }

    this.removeTokenModalLGPD(user);
    const modal = this.modalService.open(LgpdAceiteModal, { backdrop: 'static', keyboard: false, windowClass: 'modal-delete' });
    modal.componentInstance.lgpd = user.lgpdAceite;
    const componentInstance = modal.componentInstance;
    modal.result.then(() => {
      if (user.contas.length > 1) {
        this.authService.cleanTokenLGPD();
        this.sidebarService.createRoutes();
      }

      this.saveAcceptedLgpd(componentInstance, user, accountSelected, doRefresh);
    }).catch(() => {
      if (this.router.url.indexOf('/pages/login') > -1) {
        this.authService.cleanToken();
        this.tenantService.limparTenantStorage();
        this.accountService.cleanToken();
      }

      if (this.router.url.indexOf('/conta/select-conta') > -1) {
        this.tenantService.limparTenantStorage();
        this.accountService.cleanToken();
      }
    });
  }

  public async saveAcceptedLgpd(componentInstance, user, accountSelected, doRefresh) {
    componentInstance.showProgressSpinner();
    const data = await this.lgpdService.salvarLgpdAceite({ userAgent: navigator.userAgent }).catch(() => {
      this.removeTokenModalLGPD(user);
      this.toastrService.errorWithTimeClose("Erro ao salvar aceite.");
    });

    componentInstance.hideProgressSpinner();
    if (data && data.id)
      this.registerUser(user, accountSelected, doRefresh);
  }

  public removeTokenModalLGPD(user) {
    if (user.contas.length > 1) {
      // /conta/select-conta pertence ao React Router; router.navigate seria
      // ignorado pelo ReactAwareUrlHandlingStrategy. Cruza a fronteira via hash.
      redirectAcrossReactBoundary("/conta/select-conta");
      setTimeout(() => {
        this.sidebarService.clearRoutes();
      });
    }
  }

  public async registerUser(user, accountSelected, doRefresh) {
    if (user.usuarioConfig) {
      const locale = user.usuarioConfig.locale;
      this.translateService.use(locale ? `${locale.language}_${locale.country}` : this.translateService.defaultLang);
    } else {
      const locale = await this.localeService.getLocaleUsuarioOrIntegrador().toPromise();
      this.translateService.use(locale ? `${locale.language}_${locale.country}` : this.translateService.defaultLang);
    }

    if (user.acessoBloqueado) {
      this.toastrService.warning(this.translateService.instant("O acesso à conta está bloqueado temporariamente."));
      return
    }

    if (accountSelected || !user.contas || user.contas.length == 0) {
      this.getLogo(user, accountSelected);
    } else if (user.contas.length > 1) {
      if (!this.doNotRedirectAccountList) {
        // /conta/select-conta pertence ao React Router; router.navigate seria
        // ignorado pelo ReactAwareUrlHandlingStrategy. Cruza a fronteira via hash.
        redirectAcrossReactBoundary("/conta/select-conta");
        this.sidebarService.clearRoutesMenu();
      } else {
        this.doNotRedirectAccountList = false;
      }
    } else {
      const conta = user.contas[0];
      if (conta.acessoBloqueado) {
        this.toastrService.warning(this.translateService.instant("O acesso à conta está bloqueado temporariamente."));
        return
      }
      this.tenantService.salvarTenantStorage(conta);
      this.tenantService.salvarNomeTenantStorage(conta.nomeConta);
      if (doRefresh) {
        this.refreshToken();
      } else {
        this.getLogo(user, conta);
      }
    }
  }

  public mascararEmail(email) {
    if (!email || typeof email !== 'string' || !email.includes('@')) {
      return email;
    }

    const [parteLocal, dominio] = email.split('@');
    const primeiraLetra = parteLocal.charAt(0);
    const mascara = '*'.repeat(parteLocal.length - 1);
    return `${primeiraLetra}${mascara}@${dominio}`;
  }

  public getLogo(user, accountSelected) {
    let integradorId = undefined;
    if (!user.integrador) {
      const tenant = this.tenantService.getTenantStorage();
      if (tenant) {
        this.returnAccountConfigSkin();
        this.integratorService.getIntegradorContaLogada().pipe(takeUntil(this.destroy$)).subscribe((integrador: any) => {
          integradorId = integrador.id;
          this.loadLogo(integradorId, user, accountSelected);
        });
      } else {
        this.loadLogo(undefined, user, accountSelected);
      }
    } else {
      integradorId = user.integradorId;
      this.loadLogo(integradorId, user, accountSelected);
    }
  }

  public loadLogo(integradorId, user, accountSelected) {
    if (!integradorId || sessionStorage.getItem("logoHeader")) {
      this.goHome(user, accountSelected);
      return;
    }

    this.getCustomSkin().pipe(takeUntil(this.destroy$)).subscribe(skin => {
      this.goHome(user, accountSelected);
      if (!skin) {
        return;
      }
      let url;
      if (skin.favicon) {
        url = skin.favicon.startsWith("http");
        this.skin.favicon = (url ? "" : "data:image/x-icon;base64,") + skin.favicon;
        document.getElementById('favicon').setAttribute("href", this.skin.favicon);
      }
      if (skin.urlLogo) {
        url = skin.urlLogo.startsWith("http");
        this.skin.urlLogo = (url ? "" : "data:image/jpg;base64,") + skin.urlLogo;
      }
      sessionStorage.setItem("logoHeader", skin.logoHeader);
      sessionStorage.setItem("logoHeaderReduzido", skin.logoHeaderReduzido);
    });
  }

  returnAccountConfigSkin() {
    this.configSkinService.getSkinCor().pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data && data.cor) {
        Array.from(document.body.classList)
          .filter(cls => cls.indexOf("body-") !== -1)
          .forEach(cls => this.renderer2.removeClass(document.body, cls));

        this.renderer2.addClass(document.body, "body-" + data.cor);
      }
    });
  }

  public goHome(user, accountSelected) {
    this.authService.saveUsuarioStorage(user);

    // Chaveamento da migração do pós-login para React: sem destino guardado,
    // redireciona para a landing '/' (PostLoginRedirect), que resolve a home do
    // usuário. Troca o hash diretamente porque a rota pertence ao React Router
    // (HashRouter); router.navigate seria ignorado pelo ReactAwareUrlHandlingStrategy.
    // Respeita doNotRedirectAccountList como os demais ramos abaixo: chamadas de
    // refresh que não são login (ex.: atualizarMenu do ConfigPSIM) não devem navegar.
    if (environment.redirectLoginToReactPostLogin) {
      if (this.doNotRedirectAccountList) {
        this.doNotRedirectAccountList = false;
      } else if (this.redirecAccountList) {
        // Refresh com destino guardado: a troca de tenant partiu de uma tela
        // (visualizar pré-cadastro, relatório por conta), não do login. O destino
        // é a rota pedida, não a home — a landing '/' resolveria a home e o
        // descartaria, porque redirecAccountList é estado em memória desta
        // instância e não atravessa a fronteira React.
        //
        // Zera o destino ao consumi-lo: só refreshToken() o reatribui, e o login
        // (doWebLogin → registerUser → goHome) não passa por lá. Como o logout do
        // navbar Angular apenas limpa storage, sem desmontar a folha, esta
        // instância sobrevive e o login seguinte cairia no destino da sessão
        // anterior em vez da home.
        const destino = this.redirecAccountList;
        this.redirecAccountList = null;
        redirectAcrossReactBoundary(destino);
      } else {
        window.location.hash = '/';
      }
      return;
    }

    if (!user.integrador && user.contas.length > 0) {
      this.personService.getPessoaLogada().pipe(takeUntil(this.destroy$)).subscribe(
        (pessoa: any) => {
          this.configService.getConfiguracoes().pipe(takeUntil(this.destroy$)).subscribe((config) => {
            if (this.blockedData(config) && (pessoa.primeiroAcesso == null || pessoa.primeiroAcesso)) {
              this.authService.savePrimeiroAcesso(true);
              if (!this.doNotRedirectAccountList) {
                this.router.navigate(["/wizard"]);
              } else {
                this.doNotRedirectAccountList = false;
              }
            } else {
              this.authService.savePrimeiroAcesso(false);
              if (!this.doNotRedirectAccountList) {
                this.router.navigate(["/home"]);
              } else {
                this.doNotRedirectAccountList = false;
              }
            }
          })
        }
      )
    } else {
      if (!this.doNotRedirectAccountList) {
        if (accountSelected) {
          if (this.redirecAccountList) {
            if (this.isRunningInLeafHost) {
              redirectAcrossReactBoundary(this.redirecAccountList);
            } else {
              this.router.navigate([this.redirecAccountList]);
            }
          } else {
            this.router.navigate(["/home"]);
          }
        } else {
          if (user.integrador) {
            if (this.redirecAccountList) {
              if (this.isRunningInLeafHost) {
                redirectAcrossReactBoundary(this.redirecAccountList);
              } else {
                this.router.navigate([this.redirecAccountList]);
              }
            } else {
              this.router.navigate(["/integrador/inicio"]);
            }
          } else {

            if (localStorage.getItem('pathIntegrador')) {
              const rota = localStorage.getItem('pathIntegrador')
              setTimeout(() => { localStorage.removeItem('pathIntegrador') }, 100)
              this.router.navigate([rota])
              return
            }
            this.router.navigate(["/integrador/list-integrador"]);
          }
        }
      } else {
        this.doNotRedirectAccountList = false;
      }
    }
    if (accountSelected) {
      this.configModulesService.getConfigModulos().pipe(takeUntil(this.destroy$)).subscribe(
        (configModulos: ConfigModulos) => {
          this.configModulesService.saveConfigModulosStorage(configModulos);
          if (configModulos.habilitarPSIM) {
            this.configPSIMService.getConfig().pipe(takeUntil(this.destroy$)).subscribe(
              (configPSIM: ConfigPSIM) => {
                const sistemaPSIM = JSON.stringify(configPSIM.sistemaPSIM);
                const psimHabilitado = JSON.stringify(configPSIM.habilitar);
                sessionStorage.setItem("sistemaPSIM", sistemaPSIM);
                sessionStorage.setItem("psimHabilitado", psimHabilitado);
                sessionStorage.setItem("webhookHabilitado", JSON.stringify(configPSIM.webhookHabilitado));
                this.sidebarService.createRoutes();
              }
            )
          } else {
            this.sidebarService.createRoutes();
          }
        }
      );
      return;
    }

    this.accountService.cleanToken();
    this.sidebarService.createRoutes();
  }

  private identificarUsuarioPostHog(user, accountSelected): void {
    const tenantId = accountSelected?.id ?? this.tenantService.getTenantStorage()?.id;
    this.identificacaoPostHog.identificar(user, tenantId);
  }

  // Só OBRIGATORIA liga a v2: com a capability ausente ou falha na carga, o web fica no v1.
  private usarLoginV2(): boolean {
    return this.featureFlagService.verificarFlagAtiva(FeatureFlagChave.LOGIN_WEB_V2) === StatusFeatureFlag.OBRIGATORIA;
  }

  // O saveToken grava o objeto inteiro no sessionStorage; os campos extras da v2 (contas pode ser
  // grande para integrador) ficam fora para o token persistido ser o mesmo do v1.
  private semCamposLoginV2(resposta: unknown): unknown {
    if (!resposta || typeof resposta !== 'object') return resposta;
    const token: Record<string, unknown> = { ...resposta };
    delete token.nome;
    delete token.primeiroAcesso;
    delete token.contas;
    return token;
  }

  private inferirMotivoLoginFalhou(error: any): MotivoLoginFalhou {
    if (!error) return 'erro_servidor';
    if (error.name === 'TimeoutError') return 'timeout';
    const chave = error.error?.key;
    if (chave) return MOTIVO_POR_CHAVE_ERRO[chave] ?? 'erro_servidor';
    const motivoV1 = MOTIVO_POR_ERRO_V1[error.error?.error];
    if (motivoV1) return motivoV1;
    const status = error.status ?? error.error?.status;
    if (status === 401) return 'credenciais_invalidas';
    if (status === 403) return 'usuario_bloqueado';
    if (status === 0) return 'erro_rede';
    return 'erro_servidor';
  }

  blockedData(config: Configuracoes): any {
    if (!config.editarMeusDados) {
      this.personService.getRegistarAcessoPessoaLogada().pipe(takeUntil(this.destroy$)).subscribe();
    }
    return config.editarMeusDados;
  }

  getCustomSkin(): Observable<any> {
    const urlIntegrador = { url: window.location.href.split('//')[1].split('/')[0] }
    return this.post("public/custom_skin/", urlIntegrador);
  }

  getConfigIntegrador(uuid: string): Observable<any> {
    return this.get(`public/admin/config-integrador/web/${uuid}`);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
